import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { SidebarComponent } from '../../components/sidebar/sidebar.component';
import { NavbarComponent } from '../../components/navbar/navbar.component';
import { UploadService, ExcelParseResponse } from '../../core/services/upload.service';
import { ExcelParserService } from '../../core/services/excel-parser.service';
import { ProductService } from '../../core/services/product.service';
import { CategoryService } from '../../core/services/category.service';
import { Category } from '../../models/category.interface';

interface ParsedProduct {
  _rowIndex: number;
  title: string;
  category: string;
  subCategory: string;
  collectionName?: string;
  brandGroup: string;
  materials?: string[];
  price: number;
  slashPrice: number;
  inStock: boolean;
  description: string;
  imageUrl: string;
  images: { url: string; cloudinaryId: string; order: number }[];
  // UI state
  _selected: boolean;
  _status: 'pending' | 'uploading' | 'done' | 'error';
  _error?: string;
  _imageFiles?: File[];
}

@Component({
  selector: 'app-bulk-upload',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, SidebarComponent, NavbarComponent],
  template: `
    <app-sidebar></app-sidebar>
    <app-navbar
      pageTitle="Bulk Upload"
      pageSubtitle="Import products from Excel template with multiple image support"
    ></app-navbar>

    <div class="page-wrapper">
      <main class="main-content animate-in">

      <!-- STEP INDICATOR -->
      <div class="steps-bar glass-card">
        <div class="step" [class.active]="currentStep >= 1" [class.done]="currentStep > 1">
          <div class="step-circle">{{ currentStep > 1 ? '✓' : '1' }}</div>
          <span>Upload Excel</span>
        </div>
        <div class="step-line" [class.done]="currentStep > 1"></div>
        <div class="step" [class.active]="currentStep >= 2" [class.done]="currentStep > 2">
          <div class="step-circle">{{ currentStep > 2 ? '✓' : '2' }}</div>
          <span>Review & Add Images</span>
        </div>
        <div class="step-line" [class.done]="currentStep > 2"></div>
        <div class="step" [class.active]="currentStep >= 3">
          <div class="step-circle">3</div>
          <span>Import</span>
        </div>
      </div>

      <!-- STEP 1: Upload Excel -->
      <div *ngIf="currentStep === 1" class="step-content">
        <div
          class="drop-zone glass-card"
          [class.drag-over]="isDragging"
          (dragover)="onDragOver($event)"
          (dragleave)="isDragging = false"
          (drop)="onDrop($event)"
          (click)="excelInput.click()"
          id="excel-drop-zone"
        >
          <div class="drop-icon">📊</div>
          <h3 class="font-serif title-gold">Drop your Excel file here</h3>
          <p class="sub-text">or click to browse — supports .xlsx and .csv</p>
          <div class="template-hint">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            Flow: Category → Sub-Category → Collection → Products. Auto-extracts embedded photos!
          </div>
          <input #excelInput type="file" accept=".xlsx,.xls,.csv" style="display:none" (change)="onExcelSelected($event)" id="excel-file-input" />
        </div>

        <!-- Sample Template Download Card -->
        <div class="template-download-bar glass-card">
          <div class="download-info">
            <span class="download-badge">Official Excel Template</span>
            <div>
              <strong style="color: var(--accent-gold); font-size: 14px;">Shell Muze Hierarchy Template (2026 Flow)</strong>
              <p class="sub-text" style="font-size: 12px; margin-top: 2px;">
                Pre-configured with Category → Sub-Category → Collection hierarchy, Case Materials, and embedded image extraction.
              </p>
            </div>
          </div>
          <button type="button" class="btn-gold" (click)="downloadTemplate()" id="download-template-btn">
            📥 Download Sample Excel (.xlsx)
          </button>
        </div>

        <!-- Column mapping guide -->
        <div class="column-guide glass-card">
          <h4 class="font-serif" style="font-size: 13px; color: var(--text-secondary); margin-bottom: 14px;">📋 Expected Excel Columns</h4>
          <div class="column-grid">
            <div class="column-item" *ngFor="let col of expectedColumns">
              <span class="col-name">{{ col.name }}</span>
              <span class="col-req" [class.required]="col.required">{{ col.required ? 'Required' : 'Optional' }}</span>
              <span class="col-desc sub-text">{{ col.desc }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Parsing spinner -->
      <div *ngIf="parsing" class="parsing-state glass-card">
        <div class="spinner"></div>
        <p class="sub-text">Parsing Excel file...</p>
      </div>

      <!-- STEP 2: Review Parsed Products -->
      <div *ngIf="currentStep === 2 && !parsing">

        <!-- Summary Bar -->
        <div class="summary-bar glass-card">
          <div class="summary-stat">
            <span class="stat-val">{{ parsedProducts.length }}</span>
            <span class="sub-text">Total Products</span>
          </div>
          <div class="summary-stat">
            <span class="stat-val text-green">{{ selectedCount }}</span>
            <span class="sub-text">Selected</span>
          </div>
          <div class="summary-stat" *ngIf="parseErrors.length > 0">
            <span class="stat-val text-danger">{{ parseErrors.length }}</span>
            <span class="sub-text">Errors</span>
          </div>
          <div class="summary-actions">
            <button class="btn-secondary btn-sm" (click)="selectAll(true)">Select All</button>
            <button class="btn-secondary btn-sm" (click)="selectAll(false)">Deselect All</button>
            <button class="btn-gold" (click)="currentStep = 1">← Re-upload</button>
          </div>
        </div>

        <!-- Parse Errors -->
        <div *ngIf="parseErrors.length > 0" class="error-panel glass-card">
          <h4 style="color: var(--danger); font-size: 13px; margin-bottom: 10px;">⚠️ {{ parseErrors.length }} rows skipped:</h4>
          <div *ngFor="let e of parseErrors" class="error-row">
            Row {{ e.row }}: <strong>{{ e.title || 'No title' }}</strong> — {{ e.reason }}
          </div>
        </div>

        <!-- Products Table -->
        <div class="products-table glass-card">
          <table>
            <thead>
              <tr>
                <th class="col-check">
                  <input type="checkbox" [checked]="allSelected" (change)="selectAll(!allSelected)" />
                </th>
                <th>Title</th>
                <th>Category</th>
                <th>Sub-Cat</th>
                <th>Collection</th>
                <th>Group</th>
                <th>Price</th>
                <th>Stock</th>
                <th style="width: 200px;">Images (up to 6)</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let p of parsedProducts" [class.selected-row]="p._selected" [class.done-row]="p._status === 'done'" [class.error-row-tr]="p._status === 'error'">
                <td><input type="checkbox" [(ngModel)]="p._selected" /></td>
                <td>
                  <div class="product-title-cell">
                    <strong>{{ p.title }}</strong>
                    <span class="sub-text" style="font-size:10px;">Row {{ p._rowIndex }}</span>
                  </div>
                </td>
                <td><span class="cat-chip">{{ p.category }}</span></td>
                <td class="sub-text">{{ p.subCategory || '—' }}</td>
                <td>
                  <span class="col-chip" *ngIf="p.collectionName">{{ p.collectionName }}</span>
                  <span class="sub-text" *ngIf="!p.collectionName">—</span>
                </td>
                <td class="sub-text">{{ p.brandGroup || '—' }}</td>
                <td>
                  <span class="price-val">₹{{ p.price }}</span>
                  <span class="sub-text" style="text-decoration:line-through; margin-left:4px;">₹{{ p.slashPrice }}</span>
                </td>
                <td>
                  <span class="stock-chip" [class.in-stock]="p.inStock">{{ p.inStock ? 'In Stock' : 'Out' }}</span>
                </td>
                <td>
                  <div class="image-upload-cell">
                    <!-- Server-parsed images from Excel (base64) -->
                    <div class="image-previews" *ngIf="p.images && p.images.length > 0">
                      <div *ngFor="let img of p.images; let i = index" class="img-thumb img-thumb-parsed">
                        <img [src]="img.url" [alt]="'img' + i" />
                        <div class="img-thumb-badge">{{ i === 0 ? '★' : (i+1) }}</div>
                        <button class="remove-img-btn" (click)="removeParsedImage(p, i)">×</button>
                      </div>
                    </div>
                    <!-- Additional file images (uploaded by user) -->
                    <div class="image-previews" *ngIf="p._imageFiles && p._imageFiles.length > 0">
                      <div *ngFor="let f of p._imageFiles; let i = index" class="img-thumb img-thumb-new">
                        <img [src]="getObjectUrl(f)" [alt]="'new' + i" />
                        <div class="img-thumb-badge new-badge">+</div>
                        <button class="remove-img-btn" (click)="removeImageFile(p, i)">×</button>
                      </div>
                    </div>
                    <!-- Add more button -->
                    <label class="add-img-btn" *ngIf="getTotalImageCount(p) < 6">
                      <input type="file" accept="image/*" multiple style="display:none" (change)="onImageFilesSelected($event, p)" />
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                      {{ p.images && p.images.length > 0 ? 'Add More' : 'Add Images' }}
                    </label>
                    <!-- Empty state -->
                    <span *ngIf="getTotalImageCount(p) === 0" class="no-img-hint">No images</span>
                  </div>
                </td>
                <td>
                  <span *ngIf="p._status === 'pending'" class="status-chip status-pending">Pending</span>
                  <span *ngIf="p._status === 'uploading'" class="status-chip status-uploading">Uploading...</span>
                  <span *ngIf="p._status === 'done'" class="status-chip status-done">✓ Done</span>
                  <span *ngIf="p._status === 'error'" class="status-chip status-error" [title]="p._error">Error</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Import Action -->
        <div class="import-action glass-card">
          <div class="import-info">
            <strong style="color: var(--accent-gold);">{{ selectedCount }}</strong> products selected for import
            <span class="sub-text" *ngIf="hasImagesSelected"> · Products with images will be uploaded first</span>
          </div>
          <button class="btn-gold btn-lg" [disabled]="selectedCount === 0 || importing" (click)="startImport()" id="start-import-btn">
            <span *ngIf="!importing">🚀 Import {{ selectedCount }} Product{{ selectedCount !== 1 ? 's' : '' }}</span>
            <span *ngIf="importing">Importing... ({{ importProgress }}/{{ selectedCount }})</span>
          </button>
        </div>
      </div>

      <!-- STEP 3: Done -->
      <div *ngIf="currentStep === 3" class="step-content">
        <div class="done-panel glass-card">
          <div class="done-icon">🎉</div>
          <h2 class="font-serif title-gold">Import Complete!</h2>
          <div class="done-stats">
            <div class="done-stat">
              <span class="stat-val text-green">{{ importResults.created }}</span>
              <span class="sub-text">Products Created</span>
            </div>
            <div class="done-stat" *ngIf="importResults.errors > 0">
              <span class="stat-val text-danger">{{ importResults.errors }}</span>
              <span class="sub-text">Errors</span>
            </div>
          </div>
          <div class="done-actions">
            <a routerLink="/products" class="btn-gold">View Products →</a>
            <button class="btn-secondary" (click)="resetImport()">Import More</button>
          </div>
        </div>
      </div>

    </main>
  </div>
  `,
  styles: [`
    /* Steps Bar */
    .steps-bar {
      display: flex;
      align-items: center;
      padding: 20px 32px;
      margin-bottom: 24px;
      gap: 0;
    }

    .step {
      display: flex;
      align-items: center;
      gap: 10px;
      flex: none;
    }

    .step-circle {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: rgba(255,255,255,0.05);
      border: 1.5px solid var(--border-color);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 13px;
      font-weight: 700;
      color: var(--text-muted);
      transition: all 0.2s;
    }

    .step.active .step-circle {
      background: var(--accent-gold-glow);
      border-color: var(--accent-gold);
      color: var(--accent-gold);
    }

    .step.done .step-circle {
      background: rgba(74,222,128,0.1);
      border-color: #4ADE80;
      color: #4ADE80;
    }

    .step span {
      font-size: 12.5px;
      font-weight: 600;
      color: var(--text-muted);
    }

    .step.active span { color: var(--text-primary); }

    .step-line {
      flex: 1;
      height: 1px;
      background: var(--border-color);
      margin: 0 16px;
    }

    .step-line.done { background: #4ADE80; }

    /* Drop Zone */
    .step-content { display: flex; flex-direction: column; gap: 20px; }

    .drop-zone {
      padding: 64px 32px;
      text-align: center;
      cursor: pointer;
      border: 2px dashed var(--border-color);
      transition: all 0.2s;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
    }

    .drop-zone:hover, .drop-zone.drag-over {
      border-color: var(--accent-gold);
      background: var(--accent-gold-glow);
    }

    .drop-icon { font-size: 52px; }

    .template-hint {
      display: flex;
      align-items: flex-start;
      gap: 6px;
      font-size: 11px;
      color: var(--text-muted);
      max-width: 500px;
      text-align: left;
      background: rgba(0,0,0,0.2);
      padding: 10px 14px;
      border-radius: var(--radius-md);
    }

    /* Column Guide */
    .column-guide { padding: 20px 24px; }

    .column-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
      gap: 10px;
    }

    .column-item {
      display: grid;
      grid-template-columns: auto 60px;
      grid-template-rows: auto auto;
      gap: 2px 8px;
      padding: 10px 12px;
      background: rgba(0,0,0,0.15);
      border: 1px solid var(--border-subtle);
      border-radius: var(--radius-sm);
    }

    .col-name {
      font-size: 12px;
      font-weight: 700;
      color: var(--text-primary);
      grid-row: 1;
      grid-column: 1;
    }

    .col-req {
      font-size: 9px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--text-muted);
      background: rgba(255,255,255,0.05);
      padding: 1px 6px;
      border-radius: var(--radius-full);
      text-align: center;
      align-self: center;
      grid-row: 1;
      grid-column: 2;
    }

    .col-req.required {
      color: var(--accent-gold);
      background: var(--accent-gold-glow);
    }

    .col-desc {
      font-size: 10.5px;
      grid-row: 2;
      grid-column: 1 / 3;
    }

    /* Parsing State */
    .parsing-state {
      padding: 48px;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
    }

    .spinner {
      width: 36px;
      height: 36px;
      border: 3px solid var(--border-color);
      border-top-color: var(--accent-gold);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    @keyframes spin { to { transform: rotate(360deg); } }

    /* Summary Bar */
    .summary-bar {
      display: flex;
      align-items: center;
      gap: 32px;
      padding: 18px 24px;
      margin-bottom: 20px;
      flex-wrap: wrap;
    }

    .summary-stat {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
    }

    .stat-val {
      font-size: 24px;
      font-weight: 800;
      color: var(--text-primary);
      line-height: 1;
    }

    .text-green { color: #4ADE80; }
    .text-danger { color: var(--danger); }

    .summary-actions {
      display: flex;
      gap: 10px;
      margin-left: auto;
      flex-wrap: wrap;
    }

    .btn-sm {
      padding: 8px 14px;
      font-size: 12px;
    }

    /* Error Panel */
    .error-panel {
      padding: 16px 20px;
      border-left: 3px solid var(--danger);
      margin-bottom: 20px;
    }

    .error-row {
      font-size: 11.5px;
      color: var(--text-muted);
      padding: 4px 0;
      border-bottom: 1px solid var(--border-subtle);
    }

    /* Products Table */
    .products-table {
      overflow-x: auto;
      margin-bottom: 20px;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12.5px;
    }

    thead tr {
      background: rgba(0,0,0,0.2);
    }

    th, td {
      padding: 10px 12px;
      text-align: left;
      border-bottom: 1px solid var(--border-subtle);
      vertical-align: middle;
    }

    th {
      font-size: 10.5px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--text-muted);
      white-space: nowrap;
    }

    .col-check { width: 40px; }

    tr.selected-row { background: rgba(212, 175, 55, 0.04); }
    tr.done-row { opacity: 0.7; }
    tr.error-row-tr { background: rgba(248, 113, 113, 0.05); }

    .product-title-cell { display: flex; flex-direction: column; gap: 2px; }

    .cat-chip {
      display: inline-block;
      padding: 2px 8px;
      background: rgba(212, 175, 55, 0.08);
      border: 1px solid rgba(212, 175, 55, 0.2);
      border-radius: var(--radius-full);
      font-size: 10px;
      color: var(--accent-gold);
      white-space: nowrap;
    }

    .col-chip {
      display: inline-block;
      padding: 2px 8px;
      background: rgba(147, 51, 234, 0.08);
      border: 1px solid rgba(147, 51, 234, 0.25);
      border-radius: var(--radius-full);
      font-size: 10px;
      color: #c084fc;
      white-space: nowrap;
    }

    .template-download-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      padding: 16px 20px;
      border: 1px solid rgba(212, 175, 55, 0.25);
      background: linear-gradient(135deg, rgba(212, 175, 55, 0.06), rgba(0, 0, 0, 0.2));
    }

    .download-info {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .download-badge {
      padding: 3px 8px;
      border-radius: var(--radius-full);
      background: var(--accent-gold);
      color: #000;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      white-space: nowrap;
    }

    .price-val { font-weight: 700; color: var(--text-primary); }

    .stock-chip {
      padding: 2px 8px;
      border-radius: var(--radius-full);
      font-size: 10px;
      font-weight: 700;
      background: var(--danger-bg);
      color: var(--danger);
    }

    .stock-chip.in-stock {
      background: rgba(74,222,128,0.08);
      color: #4ADE80;
    }

    /* Image Upload Cell */
    .image-upload-cell {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      align-items: center;
    }

    .image-previews { display: flex; flex-wrap: wrap; gap: 4px; }

    .img-thumb {
      width: 38px;
      height: 38px;
      border-radius: 4px;
      overflow: hidden;
      position: relative;
      border: 1px solid var(--border-color);
    }

    .img-thumb img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .remove-img-btn {
      position: absolute;
      top: 0;
      right: 0;
      background: rgba(0,0,0,0.7);
      color: #fff;
      border: none;
      font-size: 10px;
      width: 14px;
      height: 14px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    }

    .add-img-btn {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 5px 8px;
      background: rgba(255,255,255,0.04);
      border: 1px dashed var(--border-color);
      border-radius: 4px;
      font-size: 10px;
      color: var(--text-muted);
      cursor: pointer;
      white-space: nowrap;
      transition: all 0.15s;
    }

    .add-img-btn:hover {
      border-color: var(--accent-gold);
      color: var(--accent-gold);
    }

    .img-thumb-parsed {
      border-color: rgba(212, 175, 55, 0.5);
    }

    .img-thumb-new {
      border-color: rgba(96, 165, 250, 0.5);
    }

    .img-thumb-badge {
      position: absolute;
      bottom: 0;
      left: 0;
      background: rgba(0,0,0,0.75);
      color: var(--accent-gold);
      font-size: 8px;
      font-weight: 800;
      padding: 1px 3px;
      line-height: 1.4;
    }

    .img-thumb-badge.new-badge {
      color: #60a5fa;
    }

    .no-img-hint {
      font-size: 10px;
      color: var(--text-muted);
      font-style: italic;
    }
    .status-chip {
      padding: 3px 8px;
      border-radius: var(--radius-full);
      font-size: 10px;
      font-weight: 700;
    }

    .status-pending { background: rgba(255,255,255,0.05); color: var(--text-muted); }
    .status-uploading { background: rgba(99,179,237,0.1); color: #63B3ED; }
    .status-done { background: rgba(74,222,128,0.1); color: #4ADE80; }
    .status-error { background: var(--danger-bg); color: var(--danger); }

    /* Import Action */
    .import-action {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 20px 24px;
      gap: 20px;
    }

    .import-info {
      font-size: 14px;
      color: var(--text-secondary);
    }

    .btn-lg {
      padding: 14px 28px;
      font-size: 14px;
    }

    /* Done Panel */
    .done-panel {
      padding: 64px 32px;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 24px;
    }

    .done-icon { font-size: 64px; }

    .done-stats {
      display: flex;
      gap: 48px;
    }

    .done-stat {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
    }

    .done-actions {
      display: flex;
      gap: 16px;
    }
  `]
})
export class BulkUploadComponent implements OnInit {
  currentStep = 1;
  isDragging = false;
  parsing = false;
  importing = false;
  importProgress = 0;

  parsedProducts: ParsedProduct[] = [];
  parseErrors: { row: number; title?: string; reason: string }[] = [];

  importResults = { created: 0, errors: 0 };
  categories: Category[] = [];

  readonly expectedColumns = [
    { name: 'Product Title', required: true, desc: 'Display name of the product' },
    { name: 'Category', required: true, desc: 'e.g. Mobile Cases, Desk Mats, Laptop Sleeves' },
    { name: 'Sub-Category', required: true, desc: 'e.g. iPhone Cases, Samsung Cases, Automotive, Aesthetic' },
    { name: 'Collection', required: true, desc: 'e.g. Porsche Series, Lumina Glitter, 24K Gold, Urban Stealth' },
    { name: 'Model Group (Mobile Only)', required: false, desc: 'e.g. iPhone 16 Pro Max, iPhone 15 Pro Max' },
    { name: 'Case Materials', required: false, desc: 'e.g. Hard Case Armor, Frosted Polycarbonate, Micro-Woven Cloth' },
    { name: 'Selling Price (INR)', required: true, desc: 'Final selling price (number)' },
    { name: 'Actual Price (INR)', required: true, desc: 'Original MRP slash price (number)' },
    { name: 'Stock Status', required: true, desc: 'Available or Out of Stock' },
    { name: 'Image 1–6', required: false, desc: '✅ Auto-extracted from embedded Excel images! Or direct image URL' },
    { name: 'Description', required: true, desc: 'Product description & specs' }
  ];

  private objectUrlCache = new WeakMap<File, string>();

  constructor(
    private uploadService: UploadService,
    private excelParserService: ExcelParserService,
    private productService: ProductService,
    private categoryService: CategoryService
  ) {}

  downloadTemplate(): void {
    this.excelParserService.downloadSampleTemplate();
  }

  ngOnInit(): void {
    this.categoryService.getCategories().subscribe({
      next: (cats) => (this.categories = cats),
      error: (err) => console.error(err)
    });
  }

  get selectedCount(): number {
    return this.parsedProducts.filter(p => p._selected).length;
  }

  get allSelected(): boolean {
    return this.parsedProducts.length > 0 && this.parsedProducts.every(p => p._selected);
  }

  get hasImagesSelected(): boolean {
    return this.parsedProducts.some(p =>
      p._selected && (
        (p.images && p.images.length > 0) ||
        (p._imageFiles && p._imageFiles.length > 0)
      )
    );
  }

  getTotalImageCount(product: ParsedProduct): number {
    return (product.images?.length || 0) + (product._imageFiles?.length || 0);
  }

  removeParsedImage(product: ParsedProduct, index: number): void {
    product.images?.splice(index, 1);
    // Update primary imageUrl
    product.imageUrl = product.images && product.images.length > 0 ? product.images[0].url : '';
  }

  selectAll(val: boolean): void {
    this.parsedProducts.forEach(p => (p._selected = val));
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    const file = event.dataTransfer?.files[0];
    if (file) this.processExcelFile(file);
  }

  onExcelSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) this.processExcelFile(file);
  }

  async processExcelFile(file: File): Promise<void> {
    this.parsing = true;
    try {
      const res = await this.excelParserService.parseExcelFile(file);
      this.parsing = false;
      this.parsedProducts = res.products.map((p) => ({
        ...p,
        _selected: true,
        _status: 'pending' as const,
        _imageFiles: p._imageFiles || []
      }));
      this.parseErrors = res.errors;
      this.currentStep = 2;
    } catch (err: any) {
      this.parsing = false;
      alert('Failed to parse Excel file: ' + (err?.message || 'Unknown error'));
    }
  }

  onImageFilesSelected(event: Event, product: ParsedProduct): void {
    const files = Array.from((event.target as HTMLInputElement).files || []);
    if (!product._imageFiles) product._imageFiles = [];
    const remaining = 6 - this.getTotalImageCount(product);
    product._imageFiles = [...product._imageFiles, ...files.slice(0, remaining)];
  }

  removeImageFile(product: ParsedProduct, index: number): void {
    product._imageFiles?.splice(index, 1);
  }

  getObjectUrl(file: File): string {
    let url = this.objectUrlCache.get(file);
    if (!url) {
      url = URL.createObjectURL(file);
      this.objectUrlCache.set(file, url);
    }
    return url;
  }

  async startImport(): Promise<void> {
    const toImport = this.parsedProducts.filter(p => p._selected && p._status === 'pending');
    if (toImport.length === 0) return;

    this.importing = true;
    this.importProgress = 0;
    let created = 0;
    let errors = 0;

    const CONCURRENCY = 3;
    let queueIndex = 0;

    const processProduct = async (product: ParsedProduct): Promise<void> => {
      product._status = 'uploading';

      try {
        // 1. Keep any valid HTTP/Cloudinary URLs from Excel cells
        const mergedImages: { url: string; cloudinaryId: string; order: number }[] = [
          ...(product.images || []).filter(img => img.url && (img.url.startsWith('http://') || img.url.startsWith('https://')))
        ];

        // 2. Upload all local images in parallel for this product
        if (product._imageFiles && product._imageFiles.length > 0) {
          const filesToUpload = product._imageFiles.slice(0, 6 - mergedImages.length);
          const uploadPromises = filesToUpload.map(async (f) => {
            try {
              const uploadRes = await firstValueFrom(this.uploadService.uploadImage(f));
              if (uploadRes && uploadRes.imageUrl) {
                return {
                  url: uploadRes.imageUrl,
                  cloudinaryId: uploadRes.cloudinaryId || ''
                };
              }
            } catch (upErr: any) {
              console.warn(`Image upload skipped for ${product.title}:`, upErr?.message || upErr);
            }
            return null;
          });

          const uploadedResults = await Promise.all(uploadPromises);
          for (const item of uploadedResults) {
            if (item) {
              mergedImages.push({
                url: item.url,
                cloudinaryId: item.cloudinaryId,
                order: mergedImages.length
              });
            }
          }
        }

        // 3. Build product payload
        const payload: any = {
          title: product.title,
          category: (product.category || '').toLowerCase().replace(/\s+/g, '-'),
          subCategory: product.subCategory,
          collectionName: product.collectionName || '',
          brandGroup: product.brandGroup,
          materials: product.materials && product.materials.length > 0 ? product.materials : undefined,
          price: product.price,
          slashPrice: product.slashPrice,
          inStock: product.inStock,
          description: product.description,
          images: mergedImages,
          imageUrl: mergedImages.length > 0 ? mergedImages[0].url : (product.imageUrl || ''),
          cloudinaryId: mergedImages.length > 0 ? mergedImages[0].cloudinaryId : '',
          badge: 'New',
          label: 'SHELL MUZE',
          icon: '✨'
        };

        // 4. Create product
        await firstValueFrom(this.productService.createProduct(payload));
        product._status = 'done';
        created++;
      } catch (err: any) {
        product._status = 'error';
        product._error = err?.error?.message || err?.message || 'Unknown error';
        errors++;
      } finally {
        this.importProgress++;
      }
    };

    const worker = async (): Promise<void> => {
      while (queueIndex < toImport.length) {
        const nextIdx = queueIndex++;
        const product = toImport[nextIdx];
        if (product) {
          await processProduct(product);
        }
      }
    };

    const workers = Array.from({ length: Math.min(CONCURRENCY, toImport.length) }, () => worker());
    await Promise.all(workers);

    this.importing = false;
    this.importResults = { created, errors };
    this.currentStep = 3;
  }

  resetImport(): void {
    this.currentStep = 1;
    this.parsedProducts = [];
    this.parseErrors = [];
    this.importResults = { created: 0, errors: 0 };
    this.importProgress = 0;
  }
}
